import React, { useMemo } from 'react';
import {
  CONTRIBUTIONS,
  INTERACTION_NODES,
  INTERACTION_PENALTIES,
  SIGNAL_METADATA,
  SOURCE_LABELS,
  SOURCE_ORDER,
  MAX_VISIBLE_SENSORS,
  Contribution,
  InteractionNode,
  extractMultiplier,
  formatWithUnit,
  selectVisibleContributions,
  INSIGHT_RISK,
  VIEW_W,
  SRC_X, SRC_W, SRC_H,
  SIG_X, SIG_W, SIG_H,
  INT_X, INT_W, INT_H,
  INS_X, INS_W, INS_H,
  DIV1_X, DIV2_X, DIV3_X,
} from './landing-signal-flow-data';

export type FocusStage = 0 | 1 | 2 | 3 | 4;

interface LandingSignalFlowProps {
  focusStage: FocusStage;
  animate: boolean;
}

// ─── Edge visual helpers ───────────────────────────────────────────────
function edgeColor(risk: number): string {
  if (risk < 0.15) return '#4ade80';
  if (risk < 0.3)  return '#ffffff';
  return '#ef4444';
}
function edgeThickness(w: number): number {
  // Floor at 1.4 so low-weight edges from single-signal sources (e.g., Force
  // Plates → CMJ Asym) still read as a real connecting line rather than a
  // sub-pixel ghost at the 1.7x zoom used on focused stages.
  return Math.max(1.4, Math.min(3, w * 15));
}
function edgeOpacity(risk: number, w: number): number {
  if (risk < 0.15) return 0.35 + w * 0.3;
  if (risk < 0.3)  return 0.28 + w * 0.15;
  return 0.50 + Math.min(w * 2, 0.5);
}
function hasGlow(w: number): boolean {
  return w > 0.03;
}

// ─── Component ─────────────────────────────────────────────────────────
export const LandingSignalFlow: React.FC<LandingSignalFlowProps> = ({ focusStage, animate }) => {
  const { riskPct, tier, deltaPct, layout } = useMemo(() => buildLayout(), []);
  const {
    visibleContribs,
    signalPositions,
    visibleInteractions,
    interactionPositions,
    sourceGroups,
    sourcePositions,
    insightY,
    contentHeight,
  } = layout;

  // Build lookup map for O(1) contribution access inside render loops
  const contribMap: Record<string, Contribution> = {};
  visibleContribs.forEach((c) => { contribMap[c.signal] = c; });

  const tierColors: Record<string, string> = {
    LOW: '#4ade80', MODERATE: '#eab308', ELEVATED: '#f97316', HIGH: '#ef4444', CRITICAL: '#991b1b',
  };
  const tierColor = tierColors[tier] || '#ef4444';
  const green = '#4ade80';

  // ─── Column dim/focus gating ────────────────────────────────────
  const colOpacity = (col: number): number => {
    if (focusStage === 0) return 1;
    if (focusStage === col) return 1;
    const diff = Math.abs(focusStage - col);
    if (diff === 1) return 0.35;
    return 0.12;
  };
  const labelColor = (i: number): string => {
    if (focusStage === 0) return '#8a8a8a';
    if (focusStage === i + 1) return '#3b82f6';
    return '#505050';
  };

  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${contentHeight}`}
      width="100%"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="CGM Sports signal flow diagram: sensors feed signals, signals combine into interactions, interactions produce an injury-risk insight."
      style={{ display: 'block', fontFamily: 'Inter, system-ui, sans-serif' }}
    >
      <defs>
        {/* userSpaceOnUse so filters apply to degenerate (zero-height)
            paths too — a straight horizontal Force Plates -> CMJ edge
            otherwise produces a 0-height filter region and the stroke
            vanishes entirely. */}
        <filter id="landing-glow-red" filterUnits="userSpaceOnUse" x="-40" y="-40" width={VIEW_W + 80} height={contentHeight + 80}>
          <feGaussianBlur stdDeviation="2" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="landing-glow-green" filterUnits="userSpaceOnUse" x="-40" y="-40" width={VIEW_W + 80} height={contentHeight + 80}>
          <feGaussianBlur stdDeviation="2" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {/* Column dividers */}
      {[DIV1_X, DIV2_X, DIV3_X].map((x) => (
        <line key={x} x1={x} y1={8} x2={x} y2={contentHeight - 8} stroke="#1f1f1f" strokeWidth="1" />
      ))}

      {/* Column labels — y matches the bottom padding below the last
          signal so the diagram is vertically balanced. */}
      {[
        { x: SRC_X + SRC_W / 2, label: 'SENSORS' },
        { x: SIG_X + SIG_W / 2, label: 'SIGNALS' },
        { x: INT_X + INT_W / 2, label: 'INTERACTIONS' },
        { x: INS_X + INS_W / 2, label: 'INSIGHT' },
      ].map((col, i) => (
        <text
          key={col.label}
          x={col.x}
          y={40}
          textAnchor="middle"
          fill={labelColor(i)}
          style={{ letterSpacing: '0.12em', textTransform: 'uppercase', transition: 'fill .5s' }}
          fontSize="9"
          fontWeight="500"
        >
          {col.label}
        </text>
      ))}

      <SensorGroup
        sourceGroups={sourceGroups}
        sourcePositions={sourcePositions}
        signalPositions={signalPositions}
        contribMap={contribMap}
        animate={animate}
        opacity={colOpacity(1)}
        greenColor={green}
      />

      <SignalGroup
        visibleContribs={visibleContribs}
        signalPositions={signalPositions}
        opacity={colOpacity(2)}
        greenColor={green}
      />

      <SignalInteractionEdges
        visibleInteractions={visibleInteractions}
        interactionPositions={interactionPositions}
        signalPositions={signalPositions}
        contribMap={contribMap}
        animate={animate}
        colOpacity={colOpacity}
        greenColor={green}
      />

      <InteractionGroup
        visibleInteractions={visibleInteractions}
        interactionPositions={interactionPositions}
        opacity={colOpacity(3)}
      />

      <InteractionInsightEdges
        visibleInteractions={visibleInteractions}
        interactionPositions={interactionPositions}
        contribMap={contribMap}
        insightY={insightY}
        animate={animate}
        colOpacity={colOpacity}
      />

      <InsightNode
        insightY={insightY}
        tierColor={tierColor}
        tier={tier}
        riskPct={riskPct}
        deltaPct={deltaPct}
        greenColor={green}
        opacity={colOpacity(4)}
      />
    </svg>
  );
};

// ─── Layout computation ────────────────────────────────────────────────
function buildLayout() {
  const visibleContribs = selectVisibleContributions(CONTRIBUTIONS);
  const VIEW_H_BASE = 480;
  // TOP_PAD matches the bottom padding applied when deriving contentHeight
  // below, so the gap above the column labels equals the gap below the
  // last signal.
  const TOP_PAD = 40;
  const sigSpacing = Math.max(32, Math.min(42, (VIEW_H_BASE - 60) / Math.max(visibleContribs.length, 1)));
  const totalSigH = visibleContribs.length * sigSpacing;
  const sigStartY = Math.max(TOP_PAD + 28, (VIEW_H_BASE - totalSigH) / 2 + TOP_PAD);

  const signalPositions: Record<string, number> = {};
  visibleContribs.forEach((c, i) => {
    signalPositions[c.signal] = sigStartY + i * sigSpacing + SIG_H / 2;
  });

  const sigKeySet = new Set(visibleContribs.map((c) => c.signal));
  const visibleInteractions = INTERACTION_NODES.filter((n) => n.signals.some((s) => sigKeySet.has(s)));

  const sigColTop = sigStartY;
  const sigColBottom = sigStartY + (visibleContribs.length - 1) * sigSpacing + SIG_H;
  const intAvail = sigColBottom - sigColTop;
  const intSpacing = intAvail / Math.max(visibleInteractions.length + 1, 2);

  const interactionPositions: Record<string, number> = {};
  visibleInteractions.forEach((n, i) => {
    interactionPositions[n.id] = sigColTop + intSpacing * (i + 1) - INT_H / 2;
  });

  const sourceGroups: Record<string, string[]> = {};
  visibleContribs.forEach((c) => {
    const src = SIGNAL_METADATA[c.signal]?.sources?.[0] || 'wearable';
    if (!sourceGroups[src]) sourceGroups[src] = [];
    sourceGroups[src].push(c.signal);
  });

  const sourcePositions: Record<string, number> = {};
  for (const src of SOURCE_ORDER) {
    const signals = sourceGroups[src] || [];
    if (!signals.length) continue;
    const ys = signals.map((s) => signalPositions[s]);
    sourcePositions[src] = ys.reduce((a, b) => a + b, 0) / ys.length;
  }

  // Prevent sensor-node overlap — enforce min 12px gap
  const sorted = Object.entries(sourcePositions).sort((a, b) => a[1] - b[1]);
  for (let i = 1; i < sorted.length; i++) {
    const prevBot = sorted[i - 1][1] + SRC_H / 2;
    const currTop = sorted[i][1] - SRC_H / 2;
    if (currTop < prevBot + 12) {
      sorted[i][1] = prevBot + 12 + SRC_H / 2;
      sourcePositions[sorted[i][0]] = sorted[i][1];
    }
  }

  // Enforce sensor cap
  const cappedSources = SOURCE_ORDER.filter((s) => sourcePositions[s] !== undefined).slice(0, MAX_VISIBLE_SENSORS);
  const cappedSourcePositions: Record<string, number> = {};
  cappedSources.forEach((s) => { cappedSourcePositions[s] = sourcePositions[s]; });

  const insightY = (sigColTop + sigColBottom) / 2 - INS_H / 2;
  const contentHeight = Math.max(sigColBottom + 40, insightY + INS_H + 40, 300);

  const riskPct = Math.round(INSIGHT_RISK.score);
  const deltaPct = Math.round(INSIGHT_RISK.score - INSIGHT_RISK.previousScore);

  return {
    riskPct,
    tier: INSIGHT_RISK.tier,
    deltaPct,
    layout: {
      visibleContribs,
      signalPositions,
      visibleInteractions,
      interactionPositions,
      sourceGroups,
      sourcePositions: cappedSourcePositions,
      insightY,
      contentHeight,
    },
  };
}

// ─── Subcomponents ─────────────────────────────────────────────────────
// Keep render fragments in dedicated components so each stays under ~60 lines.

interface SensorGroupProps {
  sourceGroups: Record<string, string[]>;
  sourcePositions: Record<string, number>;
  signalPositions: Record<string, number>;
  contribMap: Record<string, Contribution>;
  animate: boolean;
  opacity: number;
  greenColor: string;
}

const SensorGroup: React.FC<SensorGroupProps> = ({
  sourceGroups, sourcePositions, signalPositions, contribMap, animate, opacity, greenColor,
}) => (
  <g opacity={opacity} style={{ transition: 'opacity .5s' }}>
    {SOURCE_ORDER.filter((s) => sourcePositions[s] !== undefined).map((src) => {
      const srcCY = sourcePositions[src];
      const signals = sourceGroups[src] || [];
      const srcLabel = SOURCE_LABELS[src] || src;
      return (
        <g key={src}>
          {signals.map((signal, i) => {
            const c = contribMap[signal]; if (!c) return null;
            const sigY = signalPositions[signal];
            const color = edgeColor(c.risk_score);
            const t = edgeThickness(c.weighted_contribution);
            const op = edgeOpacity(c.risk_score, c.weighted_contribution);
            const glow = hasGlow(c.weighted_contribution);
            const pathId = `landing-e-ss-${src}-${signal}`;
            const dur = (2.6 + (i % 3) * 0.4).toFixed(2);
            const delay = (-i * 0.55).toFixed(2);
            return (
              <g key={signal}>
                <path
                  id={pathId}
                  d={`M${SRC_X + SRC_W},${srcCY} C${SRC_X + SRC_W + 40},${srcCY} ${SIG_X - 20},${sigY} ${SIG_X},${sigY}`}
                  stroke={color}
                  strokeWidth={t}
                  strokeOpacity={op}
                  fill="none"
                  filter={glow ? (c.risk_score > 0.3 ? 'url(#landing-glow-red)' : c.risk_score < 0.15 ? 'url(#landing-glow-green)' : undefined) : undefined}
                />
                {animate && c.weighted_contribution > 0.005 && (
                  <circle
                    r={Math.max(1.2, Math.min(2.2, t * 1.1))}
                    fill={color}
                    opacity={0.95}
                    filter={c.risk_score > 0.3 ? 'url(#landing-glow-red)' : c.risk_score < 0.15 ? 'url(#landing-glow-green)' : undefined}
                  >
                    <animateMotion dur={`${dur}s`} begin={`${delay}s`} repeatCount="indefinite" rotate="auto">
                      <mpath href={`#${pathId}`} />
                    </animateMotion>
                  </circle>
                )}
              </g>
            );
          })}
          <rect x={SRC_X} y={srcCY - SRC_H / 2} width={SRC_W} height={SRC_H} fill="#131313" stroke="#262626" strokeWidth="1" />
          <text x={SRC_X + SRC_W / 2} y={srcCY + 4} textAnchor="middle" fill="#8a8a8a" fontSize="9">
            {srcLabel}
          </text>
          {animate && (
            <circle
              cx={SRC_X + SRC_W - 6}
              cy={srcCY - SRC_H / 2 + 6}
              r="2"
              fill={greenColor}
              style={{ animation: 'dataPulse 2.4s ease-in-out infinite' }}
            />
          )}
        </g>
      );
    })}
  </g>
);

interface SignalGroupProps {
  visibleContribs: Contribution[];
  signalPositions: Record<string, number>;
  opacity: number;
  greenColor: string;
}

const SignalGroup: React.FC<SignalGroupProps> = ({ visibleContribs, signalPositions, opacity, greenColor }) => (
  <g opacity={opacity} style={{ transition: 'opacity .5s' }}>
    {visibleContribs.map((c) => {
      const meta = SIGNAL_METADATA[c.signal];
      const sigY = signalPositions[c.signal];
      let borderColor = '#262626';
      let valueColor = '#555';
      if (c.risk_score > 0.3) {
        borderColor = 'rgba(239,68,68,0.4)';
        valueColor = '#ef4444';
      } else if (c.risk_score < 0.15) {
        borderColor = 'rgba(74,222,128,0.4)';
        valueColor = greenColor;
      }
      const label = meta?.label || c.signal;
      const raw = meta ? formatWithUnit(c.signal, c.raw_value, meta) : c.raw_value.toFixed(1);
      return (
        <g key={c.signal}>
          <rect x={SIG_X} y={sigY - SIG_H / 2} width={SIG_W} height={SIG_H} fill="#131313" stroke={borderColor} strokeWidth="1" />
          <text x={SIG_X + 6} y={sigY - 2} fill="#8a8a8a" fontSize="8">
            {label.length > 14 ? label.slice(0, 13) + '.' : label}
          </text>
          <text x={SIG_X + 6} y={sigY + 9} fill={valueColor} fontSize="8" fontFamily="JetBrains Mono, monospace">
            {raw}
          </text>
        </g>
      );
    })}
  </g>
);

interface SignalInteractionEdgesProps {
  visibleInteractions: InteractionNode[];
  interactionPositions: Record<string, number>;
  signalPositions: Record<string, number>;
  contribMap: Record<string, Contribution>;
  animate: boolean;
  colOpacity: (col: number) => number;
  greenColor: string;
}

const SignalInteractionEdges: React.FC<SignalInteractionEdgesProps> = ({
  visibleInteractions, interactionPositions, signalPositions, contribMap, animate, colOpacity, greenColor,
}) => {
  // Pre-compute which (node, signal) pair gets the SHAP badge — each
  // signal shows its badge exactly once, on its first interaction in
  // iteration order. Resolving this up front keeps the render-phase
  // mapping pure (no shared mutable state inside .flatMap()).
  const badgeOwner = new Map<string, string>();
  for (const node of visibleInteractions) {
    for (const signal of node.signals) {
      if (badgeOwner.has(signal)) continue;
      if (signalPositions[signal] === undefined) continue;
      const c = contribMap[signal];
      if (!c) continue;
      const shapVal = Math.round(c.weighted_contribution * 100);
      // Show badge on red (risk_score > 0.3) or green (risk_score < 0.15)
      // signals, matching the shapColor / edgeColor threshold pair used
      // throughout this file. The neutral-gray band (0.15..0.3) is
      // intentionally skipped — a gray "+N" chip adds noise without tier
      // signal. Sleep_score (risk_score 0.08, shapVal 1) renders here as
      // a green "-1".
      if (shapVal > 0 && (c.risk_score > 0.3 || c.risk_score < 0.15)) {
        badgeOwner.set(signal, node.id);
      }
    }
  }

  const elements = visibleInteractions.flatMap((node) =>
    node.signals.filter((s) => signalPositions[s] !== undefined).map((signal) => {
      const c = contribMap[signal]; if (!c) return null;
      const intTopY = interactionPositions[node.id];
      const intCY = intTopY + INT_H / 2;
      const sigY = signalPositions[signal];
      const color = edgeColor(c.risk_score);
      const t = edgeThickness(c.weighted_contribution);
      const op = edgeOpacity(c.risk_score, c.weighted_contribution);
      const glow = hasGlow(c.weighted_contribution);

      const shapVal = Math.round(c.weighted_contribution * 100);
      const showShap = badgeOwner.get(signal) === node.id;
      const shapLabel = c.risk_score < 0.15 ? `\u2212${shapVal}` : `+${shapVal}`;
      const shapColor = c.risk_score > 0.3 ? '#ef4444' : c.risk_score < 0.15 ? greenColor : '#888';
      const badgeX = SIG_X + SIG_W + 4;
      const badgeY = sigY - 7;
      const pathId = `landing-e-si-${node.id}-${signal}`;
      const sigIdx = node.signals.indexOf(signal);
      const dur = (3.0 + (sigIdx % 3) * 0.45).toFixed(2);
      const delay = (-sigIdx * 0.7 - 0.4).toFixed(2);

      return (
        <g
          key={`${node.id}-${signal}`}
          style={{ opacity: Math.min(1, Math.min(colOpacity(2), colOpacity(3)) + 0.1), transition: 'opacity .5s' }}
        >
          <path
            id={pathId}
            d={`M${SIG_X + SIG_W},${sigY} C${SIG_X + SIG_W + 60},${sigY} ${INT_X - 30},${intCY} ${INT_X},${intCY}`}
            stroke={color}
            strokeWidth={t}
            strokeOpacity={op}
            fill="none"
            filter={glow ? (c.risk_score > 0.3 ? 'url(#landing-glow-red)' : c.risk_score < 0.15 ? 'url(#landing-glow-green)' : undefined) : undefined}
          />
          {animate && c.weighted_contribution > 0.005 && (
            <circle
              r={Math.max(1.2, Math.min(2.2, t * 1.1))}
              fill={color}
              opacity={0.95}
              filter={c.risk_score > 0.3 ? 'url(#landing-glow-red)' : c.risk_score < 0.15 ? 'url(#landing-glow-green)' : undefined}
            >
              <animateMotion dur={`${dur}s`} begin={`${delay}s`} repeatCount="indefinite" rotate="auto">
                <mpath href={`#${pathId}`} />
              </animateMotion>
            </circle>
          )}
          {showShap && (
            <g style={{ opacity: colOpacity(2), transition: 'opacity .5s' }}>
              <rect x={badgeX} y={badgeY} width="20" height="13" fill="#0a0a0a" stroke={shapColor} strokeWidth="0.5" />
              <text x={badgeX + 10} y={badgeY + 10} textAnchor="middle" fill={shapColor} fontSize="7" fontFamily="JetBrains Mono, monospace">
                {shapLabel}
              </text>
            </g>
          )}
        </g>
      );
    })
  );
  return <>{elements}</>;
};

interface InteractionGroupProps {
  visibleInteractions: InteractionNode[];
  interactionPositions: Record<string, number>;
  opacity: number;
}

const InteractionGroup: React.FC<InteractionGroupProps> = ({ visibleInteractions, interactionPositions, opacity }) => {
  const KW: Record<string, string[]> = {
    recovery_deficit:  ['sleep', 'hrv', 'recovery'],
    overload_risk:     ['load', 'spike', 'acwr'],
    autonomic_stress:  ['rhr', 'autonomic', 'hrv declining'],
  };
  return (
    <g opacity={opacity} style={{ transition: 'opacity .5s' }}>
      {visibleInteractions.map((node) => {
        const intTopY = interactionPositions[node.id];
        const intCY = intTopY + INT_H / 2;
        const keywords = KW[node.id] || [node.label.toLowerCase().split(' ')[0]];
        const matched = INTERACTION_PENALTIES.find((p) => keywords.some((kw) => p.toLowerCase().includes(kw)));
        const mult = matched ? extractMultiplier(matched) : null;
        return (
          <g key={node.id}>
            <rect x={INT_X} y={intTopY} width={INT_W} height={INT_H} fill="#131313" stroke="#262626" strokeWidth="1" />
            <text x={INT_X + 8} y={intCY - 6} fill="#8a8a8a" fontSize="9">
              {node.label.length > 18 ? node.label.slice(0, 17) + '.' : node.label}
            </text>
            <text x={INT_X + 8} y={intCY + 8} fill="#505050" fontSize="8">
              {node.description.length > 22 ? node.description.slice(0, 21) + '.' : node.description}
            </text>
            {mult !== null && (
              <>
                <rect x={INT_X + INT_W - 28} y={intTopY - 8} width="28" height="14" fill="#0a0a0a" stroke="rgba(239,68,68,0.5)" strokeWidth="0.5" />
                <text x={INT_X + INT_W - 14} y={intTopY} textAnchor="middle" fill="#ef4444" fontSize="7" fontFamily="JetBrains Mono, monospace">
                  x{mult.toFixed(1)}
                </text>
              </>
            )}
          </g>
        );
      })}
    </g>
  );
};

interface InteractionInsightEdgesProps {
  visibleInteractions: InteractionNode[];
  interactionPositions: Record<string, number>;
  contribMap: Record<string, Contribution>;
  insightY: number;
  animate: boolean;
  colOpacity: (col: number) => number;
}

const InteractionInsightEdges: React.FC<InteractionInsightEdgesProps> = ({
  visibleInteractions, interactionPositions, contribMap, insightY, animate, colOpacity,
}) => (
  <>
    {visibleInteractions.map((node, nIdx) => {
      const intCY = interactionPositions[node.id] + INT_H / 2;
      const memberRisks = node.signals.filter((s) => contribMap[s]).map((s) => contribMap[s].risk_score);
      const memberWeights = node.signals.filter((s) => contribMap[s]).map((s) => contribMap[s].weighted_contribution);
      const avgRisk = memberRisks.length ? memberRisks.reduce((a, b) => a + b, 0) / memberRisks.length : 0.38;
      const avgWeight = memberWeights.length ? memberWeights.reduce((a, b) => a + b, 0) / memberWeights.length : 0;
      const color = edgeColor(avgRisk);
      const t = edgeThickness(avgWeight);
      const op = edgeOpacity(avgRisk, avgWeight);
      const pathId = `landing-e-ii-${node.id}`;
      const dur = (3.4 + (nIdx % 3) * 0.4).toFixed(2);
      const delay = (-nIdx * 0.9 - 0.8).toFixed(2);
      return (
        <g
          key={`ii-${node.id}`}
          style={{ opacity: Math.min(1, Math.min(colOpacity(3), colOpacity(4)) + 0.1), transition: 'opacity .5s' }}
        >
          <path
            id={pathId}
            d={`M${INT_X + INT_W},${intCY} C${INT_X + INT_W + 60},${intCY} ${INS_X - 30},${insightY + INS_H / 2} ${INS_X},${insightY + INS_H / 2}`}
            stroke={color}
            strokeWidth={t}
            strokeOpacity={op}
            fill="none"
          />
          {animate && avgWeight > 0.005 && (
            <circle
              r={Math.max(1.4, Math.min(2.4, t * 1.2))}
              fill={color}
              opacity={0.95}
              filter={avgRisk > 0.3 ? 'url(#landing-glow-red)' : avgRisk < 0.15 ? 'url(#landing-glow-green)' : undefined}
            >
              <animateMotion dur={`${dur}s`} begin={`${delay}s`} repeatCount="indefinite" rotate="auto">
                <mpath href={`#${pathId}`} />
              </animateMotion>
            </circle>
          )}
        </g>
      );
    })}
  </>
);

interface InsightNodeProps {
  insightY: number;
  tierColor: string;
  tier: string;
  riskPct: number;
  deltaPct: number | null;
  greenColor: string;
  opacity: number;
}

const InsightNode: React.FC<InsightNodeProps> = ({ insightY, tierColor, tier, riskPct, deltaPct, greenColor, opacity }) => (
  <g opacity={opacity} style={{ transition: 'opacity .5s' }}>
    <rect x={INS_X} y={insightY} width={INS_W} height={INS_H} fill="#131313" stroke={tierColor} strokeWidth="1" strokeOpacity="0.5" />
    <text x={INS_X + INS_W / 2} y={insightY + 30} textAnchor="middle" fill={tierColor} fontSize="28" fontWeight="bold" fontFamily="JetBrains Mono, monospace">
      {riskPct}
    </text>
    <text x={INS_X + INS_W / 2} y={insightY + 48} textAnchor="middle" fill={tierColor} fontSize="9" fontFamily="JetBrains Mono, monospace" style={{ opacity: 0.8 }}>
      {tier} INJURY RISK
    </text>
    {deltaPct !== null && (
      <text
        x={INS_X + INS_W / 2}
        y={insightY + 64}
        textAnchor="middle"
        fill={deltaPct > 0 ? '#ef4444' : deltaPct < 0 ? greenColor : '#555'}
        fontSize="8"
        fontFamily="JetBrains Mono, monospace"
      >
        {deltaPct > 0 ? `+${deltaPct}` : `${deltaPct}`} vs prev
      </text>
    )}
  </g>
);
